import {
  businessSessionSchema,
  classRulesViewSchema,
  groupSchema,
  markAttendanceResponseSchema,
  mergeGroupsResponseSchema,
  timetableSchema,
  bookForParticipantResponseSchema,
  type BookForParticipantResponse,
  type BusinessSession,
  type ClassRulesView,
  type SaveClassRulesRequest,
  type CreateGroupRequest,
  type Group,
  type MarkAttendanceRequest,
  type MarkAttendanceResponse,
  type MergeGroupsResponse,
  type OwnerOverride,
  type SaveGroupSlotsRequest,
  type TellClientRequest,
  type Timetable,
  type UpdateGroupRequest,
} from "@teamna/shared";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
  type QueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { z } from "zod";
import { api, newIdempotencyKey } from "../api/client.ts";
import { businessScheduleQueries, queryKeys } from "./keys.ts";

/**
 * Groups, weekly slots and the session actions of the week grid (web-app-v2.md, slice C).
 *
 * Reads come from the hooks that already exist: `useTimetable` (groups with their slots, staff and
 * participant counts), `useOwnerGroups`, `useOwnerSessions`, `useOwnerSessionAttendees`, `useTeam`
 * and `useParticipants`. This file holds the writes. Each one refreshes every list that shows the
 * thing it changed: the timetable and the setup checklist, the owner group list, and the schedule.
 */

const ownerBase = (businessId: string) => `/owner/businesses/${businessId}`;
const businessBase = (businessId: string) => `/business/businesses/${businessId}`;

function refreshGroups(queryClient: QueryClient, businessId: string): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.setup.all(businessId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.owner.groups(businessId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.owner.templates(businessId) });
}

/** Every read that shows a session, its staff or its roster. Exported for the cover hooks. */
export function refreshSessions(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.owner.sessionsAll });
  void queryClient.invalidateQueries(businessScheduleQueries);
  void queryClient.invalidateQueries({ queryKey: queryKeys.owner.attendeesAll });
  void queryClient.invalidateQueries({ queryKey: queryKeys.scheduleAll });
  void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
  void queryClient.invalidateQueries({ queryKey: queryKeys.staff.all });
}

/** POST /v1/owner/businesses/:id/groups */
export function useCreateGroup(businessId: string): UseMutationResult<Group, unknown, CreateGroupRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.post(`${ownerBase(businessId)}/groups`, input, groupSchema),
    onSuccess: () => refreshGroups(queryClient, businessId),
  });
}

/** PATCH /v1/owner/businesses/:id/groups/:groupId, which also archives one. */
export function useUpdateGroup(
  businessId: string,
): UseMutationResult<Group, unknown, { groupId: string; patch: UpdateGroupRequest }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, patch }) => api.patch(`${ownerBase(businessId)}/groups/${groupId}`, patch, groupSchema),
    onSuccess: () => {
      refreshGroups(queryClient, businessId);
      refreshSessions(queryClient);
    },
  });
}

/**
 * POST /v1/owner/businesses/:id/groups/:groupId/merge: the listed groups go into this one, with
 * their weekly times, sessions, participants and coaches, and are archived.
 */
export function useMergeGroups(
  businessId: string,
): UseMutationResult<MergeGroupsResponse, unknown, { keepGroupId: string; groupIds: string[] }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ keepGroupId, groupIds }) =>
      api.post(`${ownerBase(businessId)}/groups/${keepGroupId}/merge`, { groupIds }, mergeGroupsResponseSchema),
    onSuccess: () => {
      refreshGroups(queryClient, businessId);
      refreshSessions(queryClient);
      void queryClient.invalidateQueries({ queryKey: queryKeys.setup.participants(businessId) });
      // Packages limited to a merged level now point at the kept one.
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.products(businessId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.setup.packages(businessId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.packages(businessId) });
    },
  });
}

/** PUT /v1/business/businesses/:id/groups/:groupId/slots. Future sessions change; past ones do not. */
export function useSaveGroupSlots(
  businessId: string,
  groupId: string,
): UseMutationResult<Timetable, unknown, SaveGroupSlotsRequest["slots"]> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slots) =>
      api.put(`${businessBase(businessId)}/groups/${groupId}/slots`, { slots }, timetableSchema, {
        timeoutMs: 90_000,
      }),
    onSuccess: (timetable) => {
      queryClient.setQueryData(queryKeys.setup.timetable(businessId), timetable);
      refreshGroups(queryClient, businessId);
      refreshSessions(queryClient);
    },
  });
}

/** POST /v1/business/businesses/:id/sessions/:sessionId/staff. Null takes the staff member off. */
export function useSetSessionStaff(
  businessId: string,
): UseMutationResult<BusinessSession, unknown, { sessionId: string; staffUserId: string | null }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, staffUserId }) =>
      api.post(`${businessBase(businessId)}/sessions/${sessionId}/staff`, { staffUserId }, businessSessionSchema),
    // Also after a failure: the change can commit and a later step fail, so the screen reads what is saved.
    onSettled: () => refreshSessions(queryClient),
  });
}

/**
 * POST /v1/business/businesses/:id/sessions/:sessionId/bookings. Takes one session from the balance.
 * `override` (Add anyway, #ai-feedback 2026-10-26): when no package pays, book it owed or free.
 */
export function useAddParticipantToSession(
  businessId: string,
): UseMutationResult<BookForParticipantResponse, unknown, { sessionId: string; participantId: string; override?: OwnerOverride }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, participantId, override }) =>
      api.post(`${businessBase(businessId)}/sessions/${sessionId}/bookings`, { participantId, ...(override ? { override } : {}) }, bookForParticipantResponseSchema, {
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      refreshSessions(queryClient);
      void queryClient.invalidateQueries({ queryKey: queryKeys.setup.participants(businessId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.participantProfileAll });
    },
  });
}

/**
 * POST /v1/business/businesses/:id/sessions/:sessionId/tell-client. Tell the client: a message in
 * their private thread with the club, with a push, when no package pays for the session.
 */
export function useTellClient(
  businessId: string,
): UseMutationResult<{ messageId: string }, unknown, { sessionId: string; idempotencyKey: string } & TellClientRequest> {
  return useMutation({
    // The caller keeps one key for one draft: a second Send after a lost answer replays the first
    // message, not a second one.
    mutationFn: ({ sessionId, idempotencyKey, ...body }) =>
      api.post(`${businessBase(businessId)}/sessions/${sessionId}/tell-client`, body, z.object({ messageId: z.string() }), { idempotencyKey }),
  });
}

/**
 * PUT /v1/business/businesses/:id/sessions/:sessionId/attendance. Present, absent, or null to clear.
 * A record only: no balance moves, so only the schedule and the registers refresh.
 */
export function useMarkAttendance(
  businessId: string,
): UseMutationResult<
  MarkAttendanceResponse,
  unknown,
  { sessionId: string; marks: MarkAttendanceRequest["marks"]; roster?: string[]; method?: MarkAttendanceRequest["method"] }
> {
  const queryClient = useQueryClient();
  return useMutation({
    // `method` (I1): `checkin` for a tap on the check-in list, `headcount` for the cards, `list` for the list.
    mutationFn: ({ sessionId, marks, roster, method }) =>
      api.put(
        `${businessBase(businessId)}/sessions/${sessionId}/attendance`,
        { marks, ...(roster ? { roster } : {}), ...(method ? { method } : {}) },
        markAttendanceResponseSchema,
      ),
    onSuccess: () => refreshSessions(queryClient),
    // Refused because another device changed the session: read it again, so the screen shows it.
    onError: () => refreshSessions(queryClient),
  });
}

/** GET /v1/owner/businesses/:id/groups/:groupId/rules: the group's own booking rules and the business values. */
export function useClassRules(businessId: string, groupId: string | null): UseQueryResult<ClassRulesView> {
  return useQuery({
    queryKey: queryKeys.owner.classRules(businessId, groupId ?? "none"),
    queryFn: () => api.get(`${ownerBase(businessId)}/groups/${groupId}/rules`, classRulesViewSchema),
    enabled: groupId !== null,
  });
}

/**
 * PUT /v1/owner/businesses/:id/groups/:groupId/rules. Both null: back to the business rules. The
 * window changes what members can book, so the session lists refresh too.
 */
export function useSaveClassRules(
  businessId: string,
): UseMutationResult<ClassRulesView, unknown, { groupId: string; rules: SaveClassRulesRequest }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, rules }) => api.put(`${ownerBase(businessId)}/groups/${groupId}/rules`, rules, classRulesViewSchema),
    onSuccess: (view) => {
      queryClient.setQueryData(queryKeys.owner.classRules(businessId, view.groupId), view);
      refreshGroups(queryClient, businessId);
      refreshSessions(queryClient);
    },
  });
}
